import {
  Card,
  Flex,
  SparkleIcon,
  StarFilledIcon,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import type { ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';

import styles from './wizardResultCard.module.css';

export interface WizardResultCardProps {
  actionSlot: ReactNode;
  brand?: string;
  bullets: { text: string; type: string }[];
  categoryLabel?: ReactNode;
  imageUrl: string;
  isSponsored?: boolean;
  isStaffPick: boolean;
  name: string;
  onClick?: () => void;
  onImpress?: () => void;
  price: string;
  priceOriginal?: string;
  ratingSummary?: string | null;
  tags?: ReactNode;
}

export function WizardResultCard({
  actionSlot,
  brand,
  bullets,
  categoryLabel,
  imageUrl,
  isSponsored,
  isStaffPick,
  name,
  onClick,
  onImpress,
  price,
  priceOriginal,
  ratingSummary,
  tags,
}: WizardResultCardProps) {
  const { ref: impressionRef } = useInView({
    onChange: (inView) => {
      if (inView) {
        onImpress?.();
      }
    },
    threshold: 0.5,
    triggerOnce: true,
  });
  const maxBullets = 4;
  const visibleBullets = bullets.slice(0, maxBullets);

  const hasBadges = isStaffPick || tags;

  return (
    <div ref={impressionRef} className={styles['card']}>
      <Card flat>
        <Card.Image
          src={imageUrl}
          alt={name}
          ariaLabel={`Go to ${name}`}
          className={styles['image']}
          onClick={onClick}
        />
        {hasBadges && (
          <Flex
            className={styles['badges']}
            gap={4}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            {tags}
            {isStaffPick && (
              <Tag
                background="success-light"
                color="success-dark"
                label="Staff pick!"
                size="sm"
              />
            )}
          </Flex>
        )}
        <Flex flexDirection="column" grow={1}>
          <Flex
            ariaLabel={`Go to ${name}`}
            flexDirection="column"
            height="100%"
            mt="auto"
            onClick={onClick}
          >
            {(categoryLabel || ratingSummary) && (
              <Flex
                alignItems="center"
                className={styles['categoryLabel']}
                justifyContent="space-between"
              >
                {categoryLabel}
                {ratingSummary && (
                  <Flex alignItems="center" gap={2} ml="auto">
                    <StarFilledIcon color="gold" size="sm" />
                    <Typography variant="mini" color="text-main">
                      {ratingSummary}
                    </Typography>
                  </Flex>
                )}
              </Flex>
            )}

            {isSponsored && (
              <Typography color="grays-mid">Sponsored</Typography>
            )}

            <Typography variant="body-bold" maxLines={2}>
              {name}
            </Typography>

            {brand && <Typography color="grays-mid">{brand}</Typography>}

            {visibleBullets.length > 0 && (
              <Flex
                as="ul"
                flexDirection="column"
                className={styles['bullets']}
              >
                {visibleBullets.map((bullet, index) => (
                  <Flex
                    as="li"
                    key={`${bullet.type}-${index}`}
                    alignItems="center"
                  >
                    <SparkleIcon color="primary" size="sm" mr={4} />
                    <Typography>{bullet.text}</Typography>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>

          <div>
            <Flex alignItems="center" gap={8} my={12}>
              <Typography variant="body-bold">{price}</Typography>
              {priceOriginal && (
                <Typography color="grays-mid" strikeThrough>
                  {priceOriginal}
                </Typography>
              )}
            </Flex>
            {actionSlot}
          </div>
        </Flex>
      </Card>
    </div>
  );
}
